import { useCallback, useLayoutEffect, useRef, useState } from 'react';

export type TouchMode = 'direct' | 'trackpad';

const TOUCH_MODE_KEY = 'ra_touch_mode';
const SENSITIVITY = 1.5;
const TAP_MAX_MS = 300;
const TAP_MAX_PX = 12;
const JITTER_PX = 1.5;
const SCROLL_THROTTLE_MS = 16;

interface UseTouchInputParams {
  status: string;
  sendInput: (input: Record<string, unknown>) => Promise<void>;
  enqueueInput: (work: () => Promise<void>) => Promise<void>;
  /**
   * PR 3: compute normalized (0..1) coordinates for a touch against a
   * specific <video> element. The hook calls this with the
   * `e.currentTarget` of the touch event, which is the per-pane
   * video the user actually touched — so each pane computes its
   * coords against its own bounding rect, not a global one.
   */
  getNormalizedCoords: (
    video: HTMLVideoElement,
    clientX: number,
    clientY: number
  ) => { x: number; y: number };
  /**
   * PR 3: target the input should be tagged with. The page passes
   * the track label of the pane the user is currently interacting
   * with (extracted from e.currentTarget.dataset.trackLabel on the
   * touch event), so the agent routes the touch to the right
   * monitor's capture rect.
   */
  getTarget: (video: HTMLVideoElement) => string | undefined;
  setHasFocus: (v: boolean) => void;
}

export const useTouchInput = ({
  status,
  sendInput,
  enqueueInput,
  getNormalizedCoords,
  getTarget,
  setHasFocus,
}: UseTouchInputParams) => {
  const [touchMode, setTouchModeState] = useState<TouchMode>(() => {
    try {
      return (localStorage.getItem(TOUCH_MODE_KEY) as TouchMode) || 'direct';
    } catch {
      return 'direct';
    }
  });

  const setTouchMode = useCallback((mode: TouchMode) => {
    setTouchModeState(mode);
    try {
      localStorage.setItem(TOUCH_MODE_KEY, mode);
    } catch {}
  }, []);

  const touchStartTimeRef = useRef(0);
  const touchStartPosRef = useRef({ x: 0, y: 0 });
  const lastTouchPosRef = useRef({ x: 0, y: 0 });
  const touchMovedRef = useRef(false);
  const isTwoFingerRef = useRef(false);
  const isDraggingRef = useRef(false);
  const lastScrollPosRef = useRef({ x: 0, y: 0 });
  const lastScrollTimeRef = useRef(0);

  // Single "latest values" ref synced after every render so native event
  // handlers (stable, empty deps) always see fresh values without stale closures.
  const latestRef = useRef({
    status,
    sendInput,
    enqueueInput,
    getNormalizedCoords,
    getTarget,
    setHasFocus,
    touchMode,
  });
  useLayoutEffect(() => {
    latestRef.current = {
      status,
      sendInput,
      enqueueInput,
      getNormalizedCoords,
      getTarget,
      setHasFocus,
      touchMode,
    };
  });

  const onTouchStart = useCallback((e: TouchEvent) => {
    if (latestRef.current.status !== 'connected') return;
    e.preventDefault();
    latestRef.current.setHasFocus(true);

    const touches = e.touches;
    touchStartTimeRef.current = Date.now();
    touchMovedRef.current = false;

    if (touches.length >= 2) {
      const t0 = touches[0]!;
      const t1 = touches[1]!;
      // Cancel any ongoing left-click drag before switching to two-finger gesture.
      // The up goes through the input chain so it is ordered behind the
      // most recent move/button.down and cannot race with the upcoming
      // two-finger scroll events.
      if (isDraggingRef.current) {
        void latestRef.current.enqueueInput(() =>
          latestRef.current.sendInput({ type: 'button', button: 'left', action: 'up' })
        );
        isDraggingRef.current = false;
      }
      isTwoFingerRef.current = true;
      const avgX = (t0.clientX + t1.clientX) / 2;
      const avgY = (t0.clientY + t1.clientY) / 2;
      lastScrollPosRef.current = { x: avgX, y: avgY };
      touchStartPosRef.current = { x: avgX, y: avgY };
      return;
    }

    isTwoFingerRef.current = false;
    const t = touches[0];
    if (!t) return;
    touchStartPosRef.current = { x: t.clientX, y: t.clientY };
    lastTouchPosRef.current = { x: t.clientX, y: t.clientY };

    if (latestRef.current.touchMode === 'direct') {
      const video = e.currentTarget as HTMLVideoElement;
      const { x, y } = latestRef.current.getNormalizedCoords(video, t.clientX, t.clientY);
      const target = latestRef.current.getTarget(video);
      // Atomic: move → button.down must be ordered so the cursor lands
      // at (x, y) before the down is dispatched.
      void latestRef.current.enqueueInput(async () => {
        await latestRef.current.sendInput({ type: 'move', x, y, target });
        await latestRef.current.sendInput({
          type: 'button',
          button: 'left',
          action: 'down',
          target,
        });
      });
      isDraggingRef.current = true;
    }
  }, []);

  const onTouchMove = useCallback((e: TouchEvent) => {
    if (latestRef.current.status !== 'connected') return;
    e.preventDefault();

    const touches = e.touches;

    if (touches.length >= 2) {
      const t0 = touches[0]!;
      const t1 = touches[1]!;
      const now = Date.now();
      if (now - lastScrollTimeRef.current < SCROLL_THROTTLE_MS) return;
      lastScrollTimeRef.current = now;

      const avgX = (t0.clientX + t1.clientX) / 2;
      const avgY = (t0.clientY + t1.clientY) / 2;
      const dx = avgX - lastScrollPosRef.current.x;
      const dy = avgY - lastScrollPosRef.current.y;
      lastScrollPosRef.current = { x: avgX, y: avgY };

      const tdx = Math.abs(avgX - touchStartPosRef.current.x);
      const tdy = Math.abs(avgY - touchStartPosRef.current.y);
      if (tdx > TAP_MAX_PX || tdy > TAP_MAX_PX) touchMovedRef.current = true;

      if (Math.abs(dx) > JITTER_PX || Math.abs(dy) > JITTER_PX) {
        latestRef.current.sendInput({
          type: 'scroll',
          scrollX: -dx / 40,
          scrollY: -dy / 40,
          x: 0.5,
          y: 0.5,
        });
      }
      return;
    }

    const t = touches[0];
    if (!t) return;
    const dx = t.clientX - lastTouchPosRef.current.x;
    const dy = t.clientY - lastTouchPosRef.current.y;

    const tdx = Math.abs(t.clientX - touchStartPosRef.current.x);
    const tdy = Math.abs(t.clientY - touchStartPosRef.current.y);
    if (tdx > TAP_MAX_PX || tdy > TAP_MAX_PX) touchMovedRef.current = true;

    if (latestRef.current.touchMode === 'direct') {
      const video = e.currentTarget as HTMLVideoElement;
      const { x, y } = latestRef.current.getNormalizedCoords(video, t.clientX, t.clientY);
      const target = latestRef.current.getTarget(video);
      void latestRef.current.enqueueInput(() =>
        latestRef.current.sendInput({ type: 'move', x, y, target })
      );
    } else {
      // Trackpad: relative movement with jitter filter
      if (Math.abs(dx) > JITTER_PX || Math.abs(dy) > JITTER_PX) {
        const video = e.currentTarget as HTMLVideoElement;
        const target = latestRef.current.getTarget(video);
        void latestRef.current.enqueueInput(() =>
          latestRef.current.sendInput({
            type: 'move',
            x: -1, // signals relative mode to agent
            y: -1,
            dx: dx * SENSITIVITY,
            dy: dy * SENSITIVITY,
            target,
          })
        );
      }
    }

    lastTouchPosRef.current = { x: t.clientX, y: t.clientY };
  }, []);

  const onTouchEnd = useCallback((e: TouchEvent) => {
    if (latestRef.current.status !== 'connected') return;
    e.preventDefault();

    const elapsed = Date.now() - touchStartTimeRef.current;
    const wasTap = !touchMovedRef.current && elapsed < TAP_MAX_MS;
    const wasTwoFinger = isTwoFingerRef.current;

    if (e.touches.length === 0) {
      isTwoFingerRef.current = false;
    }

    if (wasTwoFinger) {
      if (wasTap) {
        // Two-finger tap → right click
        const video = e.currentTarget as HTMLVideoElement;
        const { x, y } = latestRef.current.getNormalizedCoords(
          video,
          touchStartPosRef.current.x,
          touchStartPosRef.current.y
        );
        const target = latestRef.current.getTarget(video);
        void latestRef.current.enqueueInput(async () => {
          await latestRef.current.sendInput({ type: 'move', x, y, target });
          await latestRef.current.sendInput({
            type: 'button',
            button: 'right',
            action: 'down',
            target,
          });
          await latestRef.current.sendInput({
            type: 'button',
            button: 'right',
            action: 'up',
            target,
          });
        });
      }
      return;
    }

    if (latestRef.current.touchMode === 'direct') {
      void latestRef.current.enqueueInput(() =>
        latestRef.current.sendInput({ type: 'button', button: 'left', action: 'up' })
      );
      isDraggingRef.current = false;
    } else {
      // Trackpad: tap = left click
      if (wasTap) {
        void latestRef.current.enqueueInput(async () => {
          await latestRef.current.sendInput({ type: 'button', button: 'left', action: 'down' });
          await latestRef.current.sendInput({ type: 'button', button: 'left', action: 'up' });
        });
      }
    }
  }, []);

  return { touchMode, setTouchMode, onTouchStart, onTouchMove, onTouchEnd };
};
